import { createContext, useContext } from 'react';
import type { ClaimsLevel } from '@/modules/idv';
import { UnlockFlow } from '@/hooks/useActionGuard';
import type { ExtraRequiredClaimUnlock } from '@/hooks/actionGuardConstraints';

export interface ChildActionGuard {
  id: string; // unique ID per child
  action: string;
  resource: string;
  canPerformAction: boolean;
  isUnlockable: boolean;
  // Verification axis: the ClaimsLevel that would unlock the base permission.
  claimsThatWouldUnlock: ClaimsLevel | null;
  // Parallel axis: the child's unmet client-side extra claim (e.g. profile attribution).
  extraClaimUnlock: ExtraRequiredClaimUnlock | null;
  // True while this child's Casbin can()/could() answers are still settling. The
  // container must not fire its auto-toast off a not-yet-resolved child, whose
  // transient (canPerformAction=false, isUnlockable=true) reads as a spurious
  // "locked but unlockable" lock.
  isLoading: boolean;
}

export interface ActionGuardContainerContextType {
  registerChild: (child: ChildActionGuard) => void;
  unregisterChild: (id: string) => void;
  // `returnFromUnlockText` (optional) is forwarded to unlock destinations that support
  // a "return here" affordance — currently the Profile Wizard Attribution step, which
  // renders a footer button with this label that sends the user back to origin.
  navigateToUnlock: (
    unlockFlow: UnlockFlow,
    returnFromUnlockText?: string
  ) => void;
  // `onUnlock` (optional) runs, and is awaited, when the toast's button is
  // tapped, before the user is sent to `unlockFlow` (see ActionGuard).
  triggerToast: (
    unlockFlow: UnlockFlow,
    message: string,
    returnFromUnlockText?: string,
    onUnlock?: (unlockFlow: UnlockFlow) => void | Promise<void>
  ) => void;
  // Present a consolation toast for `on-demand` guards on a lock the user cannot
  // self-clear. With no `route`, the toast is buttonless. With a `route`, it
  // carries a button that navigates to that in-app route (rather than an
  // UnlockFlow verification/profile destination), appending the current path as a
  // `from` query param so the destination can return here. The button's label is
  // the message's [bracketed] text, or "Go" when absent.
  triggerConsolationToast: (message: string, route?: string) => void;
}

const ActionGuardContainerContext =
  createContext<ActionGuardContainerContextType | null>(null);

export const useActionGuardContainerContext =
  (): ActionGuardContainerContextType | null => {
    return useContext(ActionGuardContainerContext);
  };

export const ActionGuardContainerProvider =
  ActionGuardContainerContext.Provider;

export { ActionGuardContainerContext };
